import moment from "moment";

import { SaveSendMessageResponse } from "@/Common/Api/Communications/Dto/SaveSendMessageResponse";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { MessageOrTaskContext } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { GlobalSendCommunicationStore } from "@/Communications/Stores/GlobalSendCommunicationStore";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { FileTypeDocument } from "@/ExportableFile/Models/FileTypes/FileTypeDocument";
import { FileTypeLabPdf } from "@/ExportableFile/Models/FileTypes/FileTypeLabPdf";
import { LabMessage } from "@/Labs/Models/LabMessage";

class SendTaskOrMessagePopupHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, this.ShowPopup);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Clinicians_Selection_Changed, this.onClinicianSelected);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Patients_Selection_Change, this.OnPatientSelected);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Patients_ClearSelection, this.ClearSelection);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Subject_Changed, this.OnSubjectChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Task_FolderChecked, this.OnFolderChecked);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Task_Type_Changed, this.OnTaskTypeChanged);

        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Start_Date_Set, this.OnStartDateChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Start_Time_Set, this.OnStartTimeChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Start_Date_Today, this.OnStartDateSetToday);

        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.End_Date_Set, this.OnEndDateChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.End_Time_Set, this.OnEndTimeChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.End_Date_Today, this.OnEndDateSetToday);

        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Type_Changed, this.OnTypeChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Priority_Changed, this.OnPriorityChanged);

        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Body_Changed, this.OnBodyChanged);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Cancel_Add, this.OnCancelNewTask);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Save, this.OnSaveNewTask);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.Submit, this.OnSubmitNewTask);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.CloseVacationPopup, this.closeVacationPopup);
        this.RegisterOperation(LocalOperations.SendTaskOrMessageModel.SendFromVacationPopup, this.sendFromVacationPopup);
    }

    private ShowPopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let docInfo = request.Get<MedicalDocumentItem>("documentInfo");
        let fromCreateView = request.Get<boolean>("fromCreateView");
        let doc = request.Get<MedicalDocumentTableItem>("document");
        let target = request.Get<MessageOrTaskContext>("target");
        let isTask = request.Get<boolean>("isTask");
        let patient = request.Get<Person>("patient");
        let Employee = request.Get<Employee>("Employee");
        let encounterId = request.Get<string>("encounterId");
        let lab = request.Get<LabMessage>("lab");
        const onSave = request.Get<(requestData: any) => Promise<SaveSendMessageResponse>>("onSave");

        if (docInfo != null)
            this.AddNewTaskWithInfoFromMedicalDocument(docInfo, store, fromCreateView, isTask ?? true);
        else if (doc != null)
            this.AddNewTaskWithInfoFromMedicalDocument(doc.Model, store, fromCreateView, isTask ?? true);
        else if (target == MessageOrTaskContext.Labs)
            this.AddNewTaskWithLabInfo(request.Get<Patient>("item"), request.Get<string>("id"), store, lab);
        else
            this.addNewMessageOrTask(isTask, target, patient, Employee, store, encounterId, onSave);
    }

    private AddNewTaskWithInfoFromMedicalDocument(doc: MedicalDocumentItem, store: GlobalSendCommunicationStore, fromCreateView: boolean, isTask: boolean): void {
        store.ResetTaskMessageModel();
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
            .set("IsPopupVisible", true)
            .set("IsTask", isTask)
            .set("TempDocument", doc)
            .set("TempAttachedLabFillerID", null)
            .set("SelectedPatients", Immutable.List(this.PreloadPatientsFromDocument(doc)))
            .set("Context", MessageOrTaskContext.Documents);

        let files = doc.Model.DocumentPathList.map(
            s => new FileTypeDocument({
                description: FileTypeDocument.getDocumentFileTypeDescription(doc.Model.Subject, s.OriginalFileName, cacheStore.GetLocalizedValueByLookupId(doc.Model.SubClassId)),
                date: moment(doc.Model.UploadDate).format("YYYY/MM/DD"),
                DocumentId: s.Id
            })
        );

        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
            .set("TempAttachment", files)
            .set("TempDocumentId", doc.Model.Id)
            .set("FromCreateView", fromCreateView);
    }

    private AddNewTaskWithLabInfo(patient: Patient, labId: string, store: GlobalSendCommunicationStore, lab: LabMessage): void {
        store.ResetTaskMessageModel();
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
            .set("IsPopupVisible", true)
            .set("IsTask", true)
            .set("TempAttachedLabFillerID", labId)
            .set("TempDocument", null)
            .set("Context", MessageOrTaskContext.Labs);

        if (patient != null) {
            store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
                .set("SelectedPatients", Immutable.List([Person.FromPatient(patient)]));
        }

        if (lab.Model.HasEncapsulatedData) {
            let files = lab.Model.LinkedEmptyEncapsulatedData.map(
                encapsulatedData => new FileTypeLabPdf({
                    EncapsulatedDataId: encapsulatedData.Guid,
                    LabfillerId: lab.Model.LabFillerID,
                    description: lab.Model.Description,
                    date: moment(lab.Model.EmittedDateTime).format("YYYY/MM/DD")
                })
            );

            store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
                .set("TempAttachment", files);
        }
    }

    private addNewMessageOrTask(
        isTask: boolean,
        target: MessageOrTaskContext,
        patient: Person,
        Employee: Employee,
        store: GlobalSendCommunicationStore,
        encounterId?: string,
        onSave?: (requestData: any) => Promise<SaveSendMessageResponse>
    ): void {
        store.ResetTaskMessageModel();

        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
            .set("IsPopupVisible", true)
            .set("TempDocument", null)
            .set("TempAttachedLabFillerID", null)
            .set("OnSave", onSave)
            .set("IsTask", isTask);

        if (Employee != null)
            store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedClinicians", Immutable.List([Person.FromEmployee(Employee)]));

        if (patient != null)
            store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedPatients", Immutable.List([patient]));

        switch (target) {
            case MessageOrTaskContext.Encounter:
                store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
                    .set("Context", MessageOrTaskContext.Encounter)
                    .set("EncounterId", encounterId);
                break;

            case MessageOrTaskContext.PopulationBasedFunding:
                store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
                    .set("Context", MessageOrTaskContext.PopulationBasedFunding);
                break;

            default:
                store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("Context", MessageOrTaskContext.Unspecified);
                break;
        }
    }

    private OnSaveNewTask(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.MessageTaskActions.SaveTask(store.SendTaskOrMessageModel);
    }

    private OnSubmitNewTask(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.MessageTaskActions.verifyEmployeesInVacation(store.SendTaskOrMessageModel.get("SelectedClinicians").toArray().map(o => o.ContactFacetId));
    }

    private onClinicianSelected(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let selectedClinician = request.Get<PersonDropdownItem>("value");
        if (selectedClinician == null || selectedClinician.Item == null)
            return;

        let selectedClinicians = store.SendTaskOrMessageModel.get("SelectedClinicians").toArray();
        if (selectedClinicians.indexOf(selectedClinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != selectedClinician.Item.ContactId);
        else
            selectedClinicians.push(selectedClinician.Item);

        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedClinicians", Immutable.List(selectedClinicians));
    }

    private OnPatientSelected(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let patientItem = request.Get<PersonDropdownItem>("value");

        let list = store.SendTaskOrMessageModel.get("SelectedPatients").toArray();

        function indexOf(id: string, collection: Person[]): number {
            let idx = -1;
            for (let i = 0; i < collection.length; i++) {
                if (id === collection[i].ContactId) {
                    idx = i;
                    break;
                }
            }
            return idx;
        }

        let idx = indexOf(patientItem.Item.ContactId, list);
        if (idx === -1) {
            list.push(patientItem.Item);
        }
        else {
            list.splice(idx, 1);
        }

        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedPatients", Immutable.List(list));
    }

    private ClearSelection(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedPatients", Immutable.List([]));
    }

    private OnSubjectChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("Subject", request.Get<string>("value"));
    }

    private OnFolderChecked(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let val = store.SendTaskOrMessageModel.get("IsFolderChecked");
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("IsFolderChecked", !val);
    }

    private OnTaskTypeChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedTypeOfTask", request.Get<string>("value"));
    }

    private OnStartDateChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let val = request.Get<Date>("value");
        let dt = new Date();
        val.setHours(dt.getHours(), dt.getMinutes());
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("StartDate", val);
    }

    private OnStartTimeChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let val = request.Get<Date>("value"); let dt = store.SendTaskOrMessageModel.get("StartDate");
        dt.setHours(val.getHours()); dt.setMinutes(val.getMinutes());
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("StartDate", dt);
    }

    private OnStartDateSetToday(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("StartDate", new Date());
    }

    private OnEndDateChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let val = request.Get<Date>("value");
        let dt = new Date();
        val.setHours(dt.getHours(), dt.getMinutes());
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("EndDate", val);
    }

    private OnEndTimeChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        let val = request.Get<Date>("value"); let dt = store.SendTaskOrMessageModel.get("EndDate");
        dt.setHours(val.getHours()); dt.setMinutes(val.getMinutes());
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("EndDate", dt);
    }

    private OnEndDateSetToday(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("EndDate", new Date());
    }

    private OnTypeChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedType", request.Get<string>("value"));
    }

    private OnPriorityChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("SelectedPriorityId", request.Get<string>("value"));
    }

    private OnBodyChanged(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("Body", request.Get<string>("value"));
    }

    private OnCancelNewTask(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel
            .set("IsPopupVisible", false)
            .set("StartDate", null)
            .set("EndDate", null)
            .set("Body", "");
    }

    private PreloadPatientsFromDocument(document: MedicalDocumentItem): Person[] {
        let patients: IndividualLight[] = document.Model.Patients;
        let tempList: Person[] = [];

        patients.forEach(value => {
            let id = value.Id;
            let person = Person.FromIndividualLight(value);
            if (tempList.filter((i: Person) => i.ContactId === id).length == 0) {
                tempList.push(person);
            }
        });

        return tempList;
    }

    private closeVacationPopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("IsInVacationPopup", false);
    }

    private sendFromVacationPopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendTaskOrMessageModel = store.SendTaskOrMessageModel.set("IsInVacationPopup", false);
        store.MessageTaskActions.SubmitTask(store.SendTaskOrMessageModel);
    }
}

export { SendTaskOrMessagePopupHandler };
